import { useState, useCallback, useMemo } from 'react';
import { Outlet, useNavigate, useLocation, useOutletContext } from 'react-router';
import { deriveServiceState } from 'markcat-domain';
import {
  Sidebar, SidebarFooter, CategoryTree, SystemCategories, SmartFilters, TagFilters,
  MobileNav, ToastContainer, useToasts, useMobileDrawer, Pennant,
} from 'markcat-ui';
import { useCategoryModel, useProcessingStatus } from 'markcat-api-client';
import type { CategoryFilterAdapter, CategoryModel } from 'markcat-api-client';
import { GracePeriodBanner } from '../components/GracePeriodBanner';
import { InstallBanner } from '../components/InstallBanner';
import { PlanBadge } from '../components/PlanBadge';
import { useUIStore } from '../stores/ui';
import styles from './AppShell.module.css';

/** Shell-owned capabilities shared with /app/* routes. */
export interface AppOutletContext {
  showToast: ReturnType<typeof useToasts>['showToast'];
  openSidebar: () => void;
  categoryFilter: CategoryFilterAdapter;
  categoryModel: CategoryModel;
}

export function useAppOutlet(): AppOutletContext {
  return useOutletContext<AppOutletContext>();
}

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  // The shell only needs the sidebar status dot — 30 s is plenty. The
  // Processing page keeps the hook's 5 s default for live queue numbers.
  const { data: processingStatus, isError: processingStatusError } = useProcessingStatus({ refetchInterval: 30_000 });
  const { toasts, showToast, dismissToast } = useToasts();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const openSidebar = useCallback(() => setSidebarOpen(true), []);
  const mobileDrawerOpen = useMobileDrawer(sidebarOpen, closeSidebar);
  const categoryModel = useCategoryModel(showToast);
  const libraryActive = location.pathname === '/app' || location.pathname === '/app/';
  const selectedCategory = useUIStore((state) => state.selectedCategory);
  const selectedSystemCat = useUIStore((state) => state.selectedSystemCat);
  const selectedSmartFilter = useUIStore((state) => state.selectedSmartFilter);
  const selectedTag = useUIStore((state) => state.selectedTag);
  const categoryFilter = useMemo<CategoryFilterAdapter>(() => ({
    selectedCategory,
    selectedSystemCat,
    selectedSmartFilter,
    selectedTag,
    selectCategory: useUIStore.getState().selectCategory,
    selectSystemCat: useUIStore.getState().selectSystemCat,
    selectSmartFilter: useUIStore.getState().selectSmartFilter,
    selectTag: useUIStore.getState().selectTag,
  }), [selectedCategory, selectedSystemCat, selectedSmartFilter, selectedTag]);

  const afterCategorySelect = useCallback(() => {
    navigate('/app');
    closeSidebar();
  }, [navigate, closeSidebar]);
  const selectCategory = useCallback((path: string | null) => {
    categoryFilter.selectCategory(path);
    afterCategorySelect();
  }, [categoryFilter, afterCategorySelect]);
  const selectSystemCategory = useCallback((id: Parameters<CategoryFilterAdapter['selectSystemCat']>[0]) => {
    categoryFilter.selectSystemCat(id);
    afterCategorySelect();
  }, [categoryFilter, afterCategorySelect]);
  const selectSmartFilter = useCallback((id: Parameters<CategoryFilterAdapter['selectSmartFilter']>[0]) => {
    categoryFilter.selectSmartFilter(id);
    afterCategorySelect();
  }, [categoryFilter, afterCategorySelect]);
  const selectTag = useCallback((tag: string | null) => {
    categoryFilter.selectTag(tag);
    afterCategorySelect();
  }, [categoryFilter, afterCategorySelect]);

  const goReview = useCallback(() => { navigate('/app/review'); closeSidebar(); }, [navigate, closeSidebar]);
  const onMobileNavigate = useCallback((route: string) => {
    // Categories is a bar action, not a route — open the drawer in place.
    if (route === '#categories') { openSidebar(); return; }
    navigate(route);
    closeSidebar();
  }, [navigate, openSidebar, closeSidebar]);

  return (
    <div className={styles.shell}>
      {/* Everything behind the open mobile drawer is inert: the drawer is
          aria-modal, so keyboard focus must not reach the page behind it. */}
      <div inert={mobileDrawerOpen} aria-hidden={mobileDrawerOpen || undefined}>
        <GracePeriodBanner />
        <InstallBanner />
      </div>
      <div className="mc-layout">
        <Sidebar
          mobileDrawerOpen={mobileDrawerOpen}
          onClose={closeSidebar}
          header={
            <div className={styles.sidebarHeader}>
              <Pennant size={24} />
              <span className={styles.logo}>Markcat</span>
            </div>
          }
          footer={
            <SidebarFooter
              serviceState={deriveServiceState(processingStatus, processingStatusError)}
              onSettingsClick={() => { navigate('/app/settings'); closeSidebar(); }}
              onStatusClick={() => { navigate('/app/processing'); closeSidebar(); }}
              planSlot={<PlanBadge />}
            />
          }
        >
          <CategoryTree
            categories={categoryModel.tree}
            selectedPath={libraryActive ? categoryFilter.selectedCategory : null}
            allSelected={libraryActive && !selectedCategory && !selectedSystemCat && !selectedSmartFilter && !selectedTag}
            onSelect={selectCategory}
            onCategoryAction={categoryModel.handleCategoryAction}
            onRecategorize={categoryModel.handleRecategorize}
          />
          <SystemCategories
            counts={categoryModel.systemCounts}
            selectedId={libraryActive ? categoryFilter.selectedSystemCat : null}
            onSelect={selectSystemCategory}
          />
          <SmartFilters
            counts={categoryModel.smartFilterCounts}
            selectedId={libraryActive ? categoryFilter.selectedSmartFilter : null}
            onSelect={selectSmartFilter}
            onOpenReview={goReview}
            reviewActive={location.pathname === '/app/review'}
          />
          <TagFilters
            counts={categoryModel.tagCounts}
            selectedTag={libraryActive ? categoryFilter.selectedTag : null}
            onSelect={selectTag}
          />
        </Sidebar>

        <div className="mc-sidebar-backdrop" data-visible={mobileDrawerOpen} onClick={closeSidebar} />

        <main
          className={`mc-content ${styles.content}`}
          inert={mobileDrawerOpen}
          aria-hidden={mobileDrawerOpen || undefined}
        >
          <Outlet context={{ showToast, openSidebar, categoryFilter, categoryModel } satisfies AppOutletContext} />
        </main>
      </div>

      <div
        className={styles.mobileOnly}
        inert={mobileDrawerOpen}
        aria-hidden={mobileDrawerOpen || undefined}
      >
        <MobileNav
          pathname={location.pathname}
          counts={categoryModel.smartFilterCounts}
          onNavigate={onMobileNavigate}
        />
      </div>

      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}
